import {customerContext} from '@theme/rs-current-company-location';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

/** Matches Gadget `DCHAIN_STATUS_CONFIG` in productStatusMap. */
const DCHAIN_STATUS = {
  ACTIVE: ['01', '04', '05', '18', '31', '51'],
  PHASE_OUT: ['08'],
  DISCONTINUED: ['02', '10'],
};

function checkForProductStatusHandles() {
  const productStatusHandles = document.querySelectorAll('[data-product-status-handle]');
  productStatusHandles.forEach((productStatusHandle) => {
    // Prefer sales_data.dchain_spec_status.id for the current sales area.
    if (applyStatusFromSalesData(productStatusHandle)) {
      return;
    }

    const handle = productStatusHandle.getAttribute('data-product-status-handle');
    if (handle) {
      const banner = productStatusHandle;
      checkProductStatuses(handle, banner);
    }

    // If the product metafield is applied
    checkBannerMetafields(productStatusHandle);
  });
}

/**
 * @returns {string}
 */
function getCurrentSalesAreaExternalId() {
  if (typeof window.getSalesAreaExternalId === 'function') {
    const fromHelper = String(window.getSalesAreaExternalId() || '').trim();
    if (fromHelper) return fromHelper;
  }

  const cand = customerContext?.sales_area;
  const saRaw = Array.isArray(cand) ? cand[0]?.fields || cand[0] : cand?.fields || cand || {};
  const fromContext = String(saRaw?.external_id ?? saRaw?.sales_area_id ?? '').trim();
  if (fromContext) return fromContext;

  return String(document.body?.dataset?.currentSalesArea ?? '').trim();
}

/**
 * @param {unknown} salesDataAttr
 * @returns {any}
 */
function parseSalesDataAttribute(salesDataAttr) {
  if (!salesDataAttr || salesDataAttr === 'null' || salesDataAttr === '') return null;
  if (typeof salesDataAttr === 'object') return salesDataAttr;
  try {
    return JSON.parse(String(salesDataAttr));
  } catch {
    return null;
  }
}

/**
 * @param {Element} banner
 * @returns {any}
 */
function getSalesDataFromBanner(banner) {
  const direct = banner.getAttribute('data-sales-data');
  if (direct) return parseSalesDataAttribute(direct);

  const scope = banner.closest('.shopify-section, .product-information, .product-details, .product-info, .result-component, atomic-product');
  const host = scope?.querySelector('[data-sales-data], [data-rtp-sales]');
  if (!host) return null;

  return parseSalesDataAttribute(
    host.getAttribute('data-sales-data') || host.getAttribute('data-rtp-sales')
  );
}

/**
 * @param {any} item
 * @returns {string}
 */
function salesAreaIdFromRow(item) {
  const sa = item?.sales_area;
  if (sa == null) return '';
  if (typeof sa === 'object') {
    return String(sa.external_id?.value ?? sa.external_id ?? sa.id ?? '').trim();
  }
  return String(sa).trim();
}

/**
 * @param {any} salesData
 * @param {string} externalId
 * @returns {any | null}
 */
function resolveSalesDataForSalesArea(salesData, externalId) {
  const parsed = parseSalesDataAttribute(salesData);
  if (!parsed) return null;

  const ext = String(externalId || '').trim();
  if (!ext) return null;

  // Prefer shared helper, but only accept an exact sales-area match (no first-row fallback).
  if (typeof window.resolveCompanySalesData === 'function') {
    const row = window.resolveCompanySalesData(parsed, ext);
    if (row && salesAreaIdsMatch(salesAreaIdFromRow(row), ext)) return row;
  }

  if (Array.isArray(parsed)) {
    return (
      parsed.find((item) => item && salesAreaIdsMatch(salesAreaIdFromRow(item), ext)) || null
    );
  }

  if (parsed && typeof parsed === 'object') {
    if (parsed.value != null) {
      return resolveSalesDataForSalesArea(parsed.value, ext);
    }
    if (salesAreaIdsMatch(salesAreaIdFromRow(parsed), ext)) {
      return parsed;
    }
  }

  return null;
}

/**
 * @param {string} rowSalesArea
 * @param {string} activeId
 * @returns {boolean}
 */
function salesAreaIdsMatch(rowSalesArea, activeId) {
  const row = String(rowSalesArea ?? '').trim();
  const active = String(activeId ?? '').trim();
  if (!active || !row) return false;
  if (row === active) return true;
  return row.toLowerCase() === active.toLowerCase();
}

/**
 * dchain_spec_status is an object: { id, description }.
 * @param {any} row
 * @returns {string}
 */
function getDchainSpecStatusId(row) {
  if (!row || typeof row !== 'object') return '';
  const raw = row.dchain_spec_status;
  if (raw == null) return '';
  if (typeof raw === 'object') {
    return String(raw.id?.value ?? raw.id ?? raw.value ?? '').trim();
  }
  return String(raw).trim();
}

/**
 * @param {string} dchainId
 * @returns {'Discontinued' | 'Phase-out' | 'Active' | null}
 */
function statusFromDchainId(dchainId) {
  const id = String(dchainId ?? '').trim();
  if (!id) return null;
  if (DCHAIN_STATUS.DISCONTINUED.includes(id)) return 'Discontinued';
  if (DCHAIN_STATUS.PHASE_OUT.includes(id)) return 'Phase-out';
  if (DCHAIN_STATUS.ACTIVE.includes(id)) return 'Active';
  return 'Active';
}

/**
 * Resolve status from sku.sales_data for the current sales area via dchain_spec_status.id
 * (10 / 02 = discontinued, 08 = phase-out).
 * @param {Element} banner
 * @returns {boolean} true when status was resolved from sales_data
 */
function applyStatusFromSalesData(banner) {
  const salesData = getSalesDataFromBanner(banner);
  if (!salesData) return false;

  const externalId = getCurrentSalesAreaExternalId();
  if (!externalId) return false;

  const row = resolveSalesDataForSalesArea(salesData, externalId);
  if (!row) return false;

  const dchainId = getDchainSpecStatusId(row);
  const status = statusFromDchainId(dchainId);
  if (!status) return false;

  displayProductStatus(status, banner);
  if (status === 'Discontinued') {
    applyDiscontinuedPresentation(banner);
  }
  return true;
}

function clickParentBreadcrumb() {
  const parentBreadcrumbLink = document.querySelector('.rs-breadcrumb__item--parent .rs-breadcrumb__link');
  if (!parentBreadcrumbLink) return false;
  parentBreadcrumbLink.click();
  return true;
}

function hasReplacementProductSkus() {
  return Boolean(document.querySelector('.rs-pdp-panel--replace-products .rs-border-sku'));
}

function redirectToParentCollection() {
  return clickParentBreadcrumb();
}

function getScrollOffset() {
  const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
  return headerHeight + 16;
}

function scrollToElement(element) {
  if (!element) return;
  const top = window.scrollY + element.getBoundingClientRect().top - getScrollOffset();
  window.scrollTo({top: Math.max(0, top), behavior: 'smooth'});
}

function activateRelatedProductsAction() {
  const hasRelatedProduct = document.querySelector('.rs-pdp-panel--replace-products .product-sku-cmir');
  if (hasRelatedProduct) {
    const replaceProductsTab = document?.querySelector('.rs-tab--replace_products');
    replaceProductsTab?.click();
    if (replaceProductsTab) {
      requestAnimationFrame(() => {
        scrollToElement(replaceProductsTab);
      });
    }
    return;
  }
  redirectToParentCollection();
}

function activateReplacementProductsTab() {
  if (!hasReplacementProductSkus()) {
    redirectToParentCollection();
    return;
  }

  const replacementProductsTab = document.getElementById('replacement-products');
  if (!replacementProductsTab) {
    redirectToParentCollection();
    return;
  }
  replacementProductsTab.click();
  requestAnimationFrame(() => {
    scrollToElement(replacementProductsTab);
  });
}

function initProductStatusBannerActions() {
  if (window.__rsProductStatusBannerActionsBound) return;
  window.__rsProductStatusBannerActionsBound = true;
  document.addEventListener('click', (event) => {
    const relatedAction = event.target && event.target.closest('.rs-product-status-banner__action-related-products');
    if (relatedAction) {
      event.preventDefault();
      activateRelatedProductsAction();
      return;
    }

    const action = event.target.closest('.rs-product-status-banner__action');
    if (!action) return;
    event.preventDefault();
    activateReplacementProductsTab();
  });
}

/**
 * @param {Element} banner
 */
function setDiscontinuedRelatedProductsAction(banner) {
  banner.innerHTML =
    '<b>DISCONTINUED.</b> This product is out of production. No direct replacements are available, but you can <button type="button" class="rs-product-status-banner__action">View Replacement Products</button>';
}

/**
 * @param {Element | null} banner
 * @param {boolean} hidden
 */
function setSignInBlockHidden(banner, hidden) {
  const scope = banner?.closest('.shopify-section, .product-information, .product-details, .product-info');
  const blocks = scope
    ? scope.querySelectorAll('.rs-sign_in__block_outer')
    : document.querySelectorAll('.rs-sign_in__block_outer');
  blocks.forEach((block) => {
    block.classList.toggle('rs-opacity-0', hidden);
  });

  const stickyBuyBoxes = document.querySelectorAll('.sticky-buy-box');
  stickyBuyBoxes.forEach((box) => {
    box.classList.toggle('rs-hidden', hidden);
  });
}

/**
 * @param {Element} banner
 */
function applyDiscontinuedPresentation(banner) {
  if (!banner.classList.contains('text--discontinued-visible')) return;

  setSignInBlockHidden(banner, true);
  // Click the replacement products tab
  const replacementProductsTab = document.getElementById('replacement-products');
  if (replacementProductsTab && hasReplacementProductSkus()) {
    // Make the replacement products tab active and scroll to the tab
    activateReplacementProductsTab();
  } else if (document.getElementById('related-products')) {
    // We need to update the message
    const discontinedBanners = document.querySelectorAll('.rs-product-status-banner__text.text--discontinued');
    discontinedBanners.forEach(setDiscontinuedRelatedProductsAction);
  } else {
    // There are no replacement products or related products
    const discontinedBanners = document.querySelectorAll('.rs-product-status-banner__text.text--discontinued');
    discontinedBanners.forEach((discontinedBanner) => {
      discontinedBanner.innerHTML = '<b>DISCONTINUED.</b> This product is out of production.';
    });
  }
}

/**
 * @param {Element} productStatusHandle
 */
function checkBannerMetafields(productStatusHandle) {
  const banner = productStatusHandle;
  if (banner.classList.contains('text--discontinued-visible') && banner.classList.contains('metafield--true')) {
    applyDiscontinuedPresentation(banner);
  }
}

/**
 * @param {string} handle
 */
function buildPayload(handle) {
  const context = customerContext;
  if (!context) return null;

  const salesArea = Array.isArray(context.sales_area) ? context.sales_area[0] : context.sales_area;

  // Extract key fields
  const salesOrg = salesArea?.fields?.sales_org_id || null;
  const distributionChannel = salesArea?.fields?.distribution_channel_id || null;
  const division = salesArea?.fields?.division_id || null;

  // Construct final payload
  return {
    sales_organization: salesOrg,
    distribution_channel: distributionChannel,
    division: division,
    handle: handle,
  };
}

/**
 * @param {string} handle
 * @param {Element | null} banner
 * @param {function(string): void} [onStatus] - Optional callback with API response ShopifySKUStatus (e.g. "Discontinued", "Phase-out", "Active")
 */
async function checkProductStatuses(handle, banner, onStatus) {
  const token = window.myJWT;
  if (!token) {
    console.error('JWT token not available');
    return;
  }

  const payload = buildPayload(handle);
  if (!payload) {
    console.error('Failed to build payload - customer context may be missing');
    return;
  }

  try {
    const res = await fetch(`${getCustomerAppBaseUrl()}/api/products/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({message: 'Unknown error'}));
      console.error('API error:', error);
      return;
    }

    const data = await res.json();
    const status = data.ShopifySKUStatus;
    displayProductStatus(status, banner);
    if (typeof onStatus === 'function') {
      onStatus(status);
    }
  } catch (err) {
    console.error('PDP visibility check failed', err);
  }
}

/**
 * @param {string} status
 * @param {Element | null} banner
 */
function displayProductStatus(status, banner) {
  if (!banner) return;

  const statusFormatted = status.toLowerCase().replace(' ', '-');

  if (statusFormatted === 'active') {
    // Liquid already resolved Discontinued / Phase-out from the sku metafields, so a later
    // Active result must not hide the banner it rendered.
    if (banner.classList.contains('metafield--true')) return;
    banner.classList.remove('text--discontinued-visible', 'text--phase-out-visible');
    banner.classList.add('rs-hidden');
    setSignInBlockHidden(banner, false);
    return;
  }

  banner.classList.remove('text--discontinued-visible', 'text--phase-out-visible');
  banner.classList.add(`text--${statusFormatted}-visible`);
  banner.classList.remove('rs-hidden');
  setSignInBlockHidden(banner, statusFormatted === 'discontinued');
}

// Wait for DOM to be ready
function initProductStatus() {
  initProductStatusBannerActions();
  const waitForData = () => {
    // Wait for DOM to be ready
    if (document.readyState === 'complete' && window.myJWT !== undefined) {
      checkForProductStatusHandles();
    } else {
      setTimeout(waitForData, 100);
    }
  };
  waitForData();
}

window.addEventListener('DOMContentLoaded', initProductStatus);

/**
 * Check product status for a single banner (e.g. Coveo result card).
 * Call this when a banner with data-product-status-handle is added to the DOM.
 * Uses the same API as PDP: POST /api/products/status with handle; shows banner when status is Discontinued or Phase-out.
 * @param {Element} banner - Element with data-product-status-handle attribute (value = product handle)
 * @param {function(string): void} [onStatus] - Optional callback with API response ShopifySKUStatus (e.g. "Discontinued", "Phase-out", "Active")
 */
window.checkProductStatusForBanner = function (banner, onStatus) {
  const salesData = getSalesDataFromBanner(banner);
  if (salesData) {
    const externalId = getCurrentSalesAreaExternalId();
    const row = externalId ? resolveSalesDataForSalesArea(salesData, externalId) : null;
    const status = row ? statusFromDchainId(getDchainSpecStatusId(row)) : null;
    if (status) {
      displayProductStatus(status, banner);
      if (status === 'Discontinued') {
        applyDiscontinuedPresentation(banner);
      }
      if (typeof onStatus === 'function') {
        onStatus(status);
      }
      return;
    }
  }

  const handle = banner.getAttribute('data-product-status-handle');
  if (handle) {
    checkProductStatuses(handle, banner, onStatus);
  }
};
